import { useState } from "react";
import { ExternalLink, Pencil } from "lucide-react";

import { cn } from "@/marketing/lib/utils";
import { Button } from "@/marketing/components/ui/button";
import { Input } from "@/marketing/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/marketing/components/ui/sheet";
import { formatDate, initials, safeHostname } from "@/marketing/lib/formatters";
import type {
  CommunityComment,
  CommunityPostStatus,
  CommunityPostView,
  CreateCommentRequest,
} from "@/marketing/models/community";

const STATUS_STYLES: Record<CommunityPostStatus, string> = {
  PUBLISHED: "tw-bg-green-100 tw-text-green-700",
  HIDDEN: "tw-bg-gray-100 tw-text-gray-500",
  REMOVED: "tw-bg-red-100 tw-text-red-700",
};

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="tw-mb-2 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-gray-400">
        {label}
      </div>
      {children}
    </div>
  );
}

function StatusPill({ status }: { status: CommunityPostStatus }) {
  return (
    <span
      className={cn(
        "tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium",
        STATUS_STYLES[status],
      )}
    >
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

interface CommunityDetailPanelProps {
  post: CommunityPostView | null;
  onClose: () => void;
  onEdit?: (post: CommunityPostView) => void;
  onHide?: (post: CommunityPostView) => void;
  // HELP posts: comments + moderation. Loaded by the parent and passed in.
  comments?: CommunityComment[] | undefined;
  commentsLoading?: boolean;
  onModerate?: (post: CommunityPostView, next: CommunityPostStatus) => void;
  // When provided, renders an admin comment composer for HELP posts.
  onPostComment?: ((request: CreateCommentRequest) => void) | undefined;
  postingComment?: boolean;
}

export function CommunityDetailPanel({
  post,
  onClose,
  onEdit,
  onHide,
  comments,
  commentsLoading,
  onModerate,
  onPostComment,
  postingComment,
}: CommunityDetailPanelProps) {
  const isPick = post?.type === "PICK";
  const typeLabel = isPick ? "★ Skin Seoul Pick" : "Help post";

  return (
    <Sheet open={!!post} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="tw-flex tw-w-[480px] tw-flex-col tw-p-0 sm:tw-max-w-[480px]">
        {post && (
          <>
            <div className="tw-border-b tw-border-gray-200 tw-p-5 tw-pr-12">
              <SheetTitle className="tw-text-base tw-font-semibold tw-leading-snug tw-text-gray-900">
                {post.title || "(untitled)"}
              </SheetTitle>
              <div className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <span
                  className={cn(
                    "tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium",
                    isPick ? "tw-bg-amber-100 tw-text-amber-700" : "tw-bg-gray-100 tw-text-gray-600",
                  )}
                >
                  {typeLabel}
                </span>
                <StatusPill status={post.status} />
                {post.campaign && (
                  <span className="tw-rounded-full tw-bg-violet-100 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-violet-700">
                    ◎ {post.campaign.name}
                    {post.campaign.brandName ? ` · ${post.campaign.brandName}` : ""}
                  </span>
                )}
              </div>
              <SheetDescription className="tw-sr-only">{typeLabel} detail</SheetDescription>
            </div>

            <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-6 tw-overflow-y-auto tw-p-5">
              <Section label="Content">
                <p className="tw-whitespace-pre-wrap tw-text-sm tw-leading-relaxed tw-text-gray-700">
                  {post.body}
                </p>
                {post.imageUrls.length > 0 && (
                  <div className="tw-mt-3 tw-grid tw-grid-cols-3 tw-gap-2">
                    {post.imageUrls.map((url, idx) => (
                      <img
                        key={url}
                        src={url}
                        alt={`Post image ${idx + 1}`}
                        className="tw-aspect-square tw-w-full tw-rounded-lg tw-object-cover"
                      />
                    ))}
                  </div>
                )}
              </Section>

              {!isPick && (
                <Section label={`Comments${comments ? ` (${comments.length})` : ""}`}>
                  {commentsLoading && (
                    <div className="tw-text-sm tw-text-gray-400">Loading comments…</div>
                  )}
                  {!commentsLoading && comments && comments.length === 0 && (
                    <div className="tw-text-sm tw-text-gray-400">No comments yet.</div>
                  )}
                  <div className="tw-flex tw-flex-col tw-gap-2">
                    {comments?.map((comment) => (
                      <div
                        key={comment.commentId}
                        className="tw-rounded-lg tw-border tw-border-gray-200 tw-p-2.5"
                      >
                        <div className="tw-mb-1 tw-flex tw-items-center tw-gap-2 tw-text-xs">
                          <span className="tw-font-semibold tw-text-gray-900">
                            user {comment.authorId}
                          </span>
                          <span className="tw-text-gray-400">
                            {formatDate(comment.createdAt)}
                          </span>
                          {comment.status !== "PUBLISHED" && (
                            <span className="tw-rounded-full tw-bg-gray-100 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-text-gray-500">
                              {comment.status.toLowerCase()}
                            </span>
                          )}
                        </div>
                        <p className="tw-text-[13px] tw-leading-relaxed tw-text-gray-700">
                          {comment.body}
                        </p>
                      </div>
                    ))}
                  </div>
                  {onPostComment && (
                    <CommentComposer onPost={onPostComment} posting={!!postingComment} />
                  )}
                </Section>
              )}

              {post.featuredContents.length > 0 && (
                <Section label="Featured content (ranked)">
                  <div className="tw-flex tw-flex-col tw-gap-2">
                    {[...post.featuredContents]
                      .sort((a, b) => a.rank - b.rank)
                      .map((item) => (
                        <div
                          key={`${item.rank}-${item.contentUrl}`}
                          className="tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-2"
                        >
                          <span className="tw-grid tw-h-5 tw-w-5 tw-flex-none tw-place-items-center tw-rounded tw-bg-gray-900 tw-text-[11px] tw-font-bold tw-text-white">
                            {item.rank}
                          </span>
                          {item.thumbnailUrl && (
                            <img
                              src={item.thumbnailUrl}
                              alt=""
                              className="tw-h-11 tw-w-11 tw-flex-none tw-rounded-lg tw-object-cover"
                            />
                          )}
                          <div className="tw-min-w-0 tw-flex-1">
                            {item.comment && (
                              <div className="tw-truncate tw-text-[13px] tw-font-medium tw-text-gray-900">
                                {item.comment}
                              </div>
                            )}
                            <a
                              href={item.contentUrl}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="tw-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-gray-400 hover:tw-text-violet-600"
                            >
                              {item.platform ? `${item.platform} · ` : ""}
                              {safeHostname(item.contentUrl)}
                              <ExternalLink className="tw-h-3 tw-w-3" />
                            </a>
                          </div>
                        </div>
                      ))}
                  </div>
                </Section>
              )}

              <Section label="Engagement">
                <div className="tw-flex tw-gap-2.5">
                  <div className="tw-flex-1 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-3">
                    <div className="tw-text-xl tw-font-bold tw-text-gray-900">{post.likeCount}</div>
                    <div className="tw-mt-0.5 tw-text-[11px] tw-text-gray-400">Likes</div>
                  </div>
                  <div className="tw-flex-1 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-3">
                    <div className="tw-text-xl tw-font-bold tw-text-gray-900">{post.commentCount}</div>
                    <div className="tw-mt-0.5 tw-text-[11px] tw-text-gray-400">Comments</div>
                  </div>
                </div>
              </Section>

              <Section label="Details">
                <div className="tw-flex tw-flex-col tw-gap-2 tw-text-[13px]">
                  <div className="tw-flex tw-justify-between">
                    <span className="tw-text-gray-400">Linked campaign</span>
                    <span className="tw-font-medium tw-text-gray-900">
                      {post.campaign?.name ?? "—"}
                    </span>
                  </div>
                  <div className="tw-flex tw-justify-between">
                    <span className="tw-text-gray-400">Published</span>
                    <span className="tw-font-medium tw-text-gray-900">
                      {formatDate(post.createdAt)}
                    </span>
                  </div>
                </div>
              </Section>

              <Section label="Author">
                <div className="tw-flex tw-items-center tw-gap-2.5">
                  {post.author.avatarUrl ? (
                    <img
                      src={post.author.avatarUrl}
                      alt=""
                      className="tw-h-8 tw-w-8 tw-flex-none tw-rounded-full tw-object-cover"
                    />
                  ) : (
                    <div className="tw-grid tw-h-8 tw-w-8 tw-flex-none tw-place-items-center tw-rounded-full tw-bg-gray-900 tw-text-[13px] tw-font-semibold tw-text-white">
                      {initials(post.author.displayName)}
                    </div>
                  )}
                  <div className="tw-min-w-0">
                    <div className="tw-truncate tw-text-[13px] tw-font-medium tw-text-gray-900">
                      {post.author.displayName ?? `user ${post.author.userId}`}
                    </div>
                    <div className="tw-text-xs tw-text-gray-400">
                      {isPick ? "Skin Seoul Team · admin" : "Creator"}
                    </div>
                  </div>
                </div>
              </Section>
            </div>

            {isPick && (onEdit || onHide) && (
              <div className="tw-flex tw-gap-2 tw-border-t tw-border-gray-200 tw-p-4">
                {onEdit && (
                  <Button
                    variant="outline"
                    className="tw-flex-1"
                    onClick={() => onEdit(post)}
                  >
                    <Pencil className="tw-mr-1 tw-h-3.5 tw-w-3.5" /> Edit pick
                  </Button>
                )}
                {onHide && post.status === "PUBLISHED" && (
                  <Button
                    variant="outline"
                    className="tw-flex-1 tw-text-red-600"
                    onClick={() => onHide(post)}
                  >
                    Hide
                  </Button>
                )}
              </div>
            )}

            {!isPick && onModerate && (
              <div className="tw-flex tw-gap-2 tw-border-t tw-border-gray-200 tw-p-4">
                {post.status === "PUBLISHED" ? (
                  <>
                    <Button
                      variant="outline"
                      className="tw-flex-1"
                      onClick={() => onModerate(post, "HIDDEN")}
                    >
                      Hide
                    </Button>
                    <Button
                      variant="outline"
                      className="tw-flex-1 tw-text-red-600"
                      onClick={() => onModerate(post, "REMOVED")}
                    >
                      Delete post
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    className="tw-flex-1"
                    onClick={() => onModerate(post, "PUBLISHED")}
                  >
                    Restore
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// Admin composer: post a comment/reply on a HELP post as a chosen user (e.g. Skin Seoul Team).
function CommentComposer({
  onPost,
  posting,
}: {
  onPost: (request: CreateCommentRequest) => void;
  posting: boolean;
}) {
  const [authorId, setAuthorId] = useState("");
  const [body, setBody] = useState("");

  const canPost =
    Number.isInteger(Number(authorId)) && Number(authorId) > 0 && body.trim() !== "" && !posting;

  const submit = () => {
    if (!canPost) return;
    onPost({ authorId: Number(authorId), body: body.trim() });
    setBody("");
  };

  return (
    <div className="tw-mt-3 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-2.5">
      <div className="tw-mb-2 tw-flex tw-items-center tw-gap-2">
        <label htmlFor="comment-author-id" className="tw-text-[11px] tw-font-medium tw-text-gray-500">
          Reply as user ID
        </label>
        <Input
          id="comment-author-id"
          value={authorId}
          onChange={(e) => setAuthorId(e.target.value)}
          inputMode="numeric"
          placeholder="e.g. 1024"
          className="tw-h-7 tw-w-24 tw-text-xs"
        />
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write a reply…"
        aria-label="Reply comment body"
        className="tw-min-h-[54px] tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-px-3 tw-py-2 tw-text-sm tw-outline-none focus:tw-border-violet-500"
      />
      <div className="tw-mt-1.5 tw-flex tw-justify-end">
        <Button size="sm" onClick={submit} disabled={!canPost}>
          {posting ? "Posting…" : "Post comment"}
        </Button>
      </div>
    </div>
  );
}
